import { resolveHttpResourceUrl } from "../../../lib/http/resourceUrl";
import { Button, Checkbox, Input, Select, Tag, Typography, Upload } from "@arco-design/web-react";
import { Plus, Trash2 } from "lucide-react";
import type { Dispatch, SetStateAction } from "react";

import { useContactRecordUpload } from "../runtime/useContactRecordUpload";
import type {
  ContactWorkbenchRecordAttachment,
  ContactWorkbenchRecordItem,
  ContactWorkbenchRecordCreatePayload,
  ContactWorkbenchRecordLabelGroup,
  ContactWorkbenchRecordTypeOption,
} from "../types";

/** 选中“需重新致电/空号”时与老系统一致，无需再填记录内容。 */
const CONTENT_HIDING_LABELS = new Set(["需重新致电", "空号"]);
/** 「其他」组电话状态类标签。 */
const OTHER_STATUS_LABELS = new Set(["无出行计划", "关机", "停机", "座机"]);
/** 「后续动作」组标签。 */
const FOLLOW_UP_LABELS = new Set([
  "需关注",
  "需加微信",
  "需发资料",
  "需上门拜访",
  "需到校开讲座",
]);

export type ContactRecordDraft = {
  recordTypeCode: string;
  meetingName: string;
  meetingDesc: string;
  selectedLabels: string[];
  content: string;
  attachments: ContactWorkbenchRecordAttachment[];
};

/** 创建联系人动态表单默认值。 */
export function createDefaultContactRecordDraft(): ContactRecordDraft {
  return {
    recordTypeCode: "tele_sales",
    meetingName: "",
    meetingDesc: "",
    selectedLabels: [],
    content: "",
    attachments: [],
  };
}

/** 把动态草稿映射为联系人工作台新增动态请求。 */
export function mapContactRecordDraftToPayload(
  draft: ContactRecordDraft,
): ContactWorkbenchRecordCreatePayload | null {
  const content = draft.content.trim();
  const meetingName = draft.meetingName.trim();
  const meetingDesc = draft.meetingDesc.trim();
  const hasMeeting = Boolean(meetingName && meetingDesc);
  const hasContentHidingLabels = draft.selectedLabels.some((label) =>
    CONTENT_HIDING_LABELS.has(label),
  );
  // 需重新致电/空号与老系统一致，允许只带标签、不填记录内容。
  if (!content && !hasMeeting && !hasContentHidingLabels) {
    return null;
  }
  return {
    recordTypeCode: draft.recordTypeCode || "other",
    content: content || undefined,
    meetingName: meetingName || undefined,
    meetingDesc: meetingDesc || undefined,
    labelsSnapshot: draft.selectedLabels.length ? draft.selectedLabels.join("▲") : undefined,
    attachments: draft.attachments.map((item) => ({
      fileUrl: item.fileUrl,
      content: item.content,
    })),
  };
}

/** 联系人动态列表，与联系人工作台共享同一结构和样式。 */
export function ContactRecordList({
  records,
  mutating,
  canDeleteRecord,
  onDelete,
}: {
  records: ContactWorkbenchRecordItem[];
  mutating: boolean;
  canDeleteRecord: boolean;
  onDelete: (record: ContactWorkbenchRecordItem) => void;
}) {
  return (
    <section className="crm-contact-workbench-detail-section crm-contact-workbench-detail-section--records">
      <Typography.Title heading={6}>动态记录</Typography.Title>
      <div className="crm-contact-workbench-record-list">
        {records.length === 0 ? (
          <Typography.Text type="secondary">暂无动态记录</Typography.Text>
        ) : (
          records.map((record) => {
            const labels = splitContactRecordLabels(record.labelsSnapshot);
            return (
              <article className="crm-contact-workbench-record-item" key={record.id}>
                <header className="crm-contact-workbench-record-item__header">
                  <Tag color="arcoblue" size="small">
                    {record.recordTypeLabel}
                  </Tag>
                  <div className="crm-contact-workbench-record-item__meta">
                    <time dateTime={record.recordedAt}>{record.recordedAt}</time>
                    <Button
                      aria-label="删除动态"
                      disabled={mutating || !canDeleteRecord}
                      icon={<Trash2 size={14} />}
                      shape="circle"
                      size="mini"
                      status="danger"
                      type="secondary"
                      onClick={() => onDelete(record)}
                    />
                  </div>
                </header>
                {record.meetingName ? (
                  <strong className="crm-contact-workbench-record-item__title">
                    {record.meetingName}
                  </strong>
                ) : null}
                <p className="crm-contact-workbench-record-item__content">
                  {record.content || record.meetingDesc || "-"}
                </p>
                {labels.length > 0 ? (
                  <div className="crm-contact-workbench-record-item__chips">
                    {labels.map((label) => (
                      <span className="crm-contact-workbench-record-chip" key={label}>
                        {label}
                      </span>
                    ))}
                  </div>
                ) : null}
                {record.attachments && record.attachments.length > 0 ? (
                  <div className="crm-contact-workbench-record-attachments">
                    {record.attachments.map((item) => (
                      <a
                        className="crm-contact-workbench-record-attachment"
                        key={item.fileUrl}
                        href={resolveHttpResourceUrl(item.fileUrl)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {isVideoUrl(item.fileUrl) ? "视频附件" : "图片附件"}
                      </a>
                    ))}
                  </div>
                ) : null}
                {record.recordedByUserName ? (
                  <footer className="crm-contact-workbench-record-item__footer">
                    记录人：{record.recordedByUserName}
                  </footer>
                ) : null}
              </article>
            );
          })
        )}
      </div>
    </section>
  );
}

/** 联系人动态新增，与联系人工作台共享同一字段和样式。 */
export function ContactRecordCreator({
  draft,
  recordTypeOptions,
  recordLabelGroups,
  mutating,
  canRecord,
  actionPlacement = "title",
  onDraftChange,
  onSubmit,
}: {
  draft: ContactRecordDraft;
  recordTypeOptions: ContactWorkbenchRecordTypeOption[];
  recordLabelGroups: ContactWorkbenchRecordLabelGroup[];
  mutating: boolean;
  canRecord: boolean;
  /** title：标题旁保存（桌面）；footer：底部固定保存（窄屏抽屉）。 */
  actionPlacement?: "title" | "footer";
  onDraftChange: Dispatch<SetStateAction<ContactRecordDraft>>;
  onSubmit: () => void;
}) {
  const { uploadProps, uploading, failed } = useContactRecordUpload(
    draft.attachments,
    (attachments) => onDraftChange((current) => ({ ...current, attachments })),
  );
  const selectedType =
    recordTypeOptions.find((option) => option.code === draft.recordTypeCode) ??
    recordTypeOptions[0] ?? {
      code: "other",
      label: "其他",
      formMode: "content",
    };
  const showLabels = selectedType.formMode === "content";
  const isMeetingMode = selectedType.formMode === "meeting";
  const isMediaMode = selectedType.formMode === "media_note";
  const hideContentByLabels =
    showLabels &&
    draft.selectedLabels.some((label) => CONTENT_HIDING_LABELS.has(label));
  const showContent = !isMeetingMode && !hideContentByLabels;
  const contentLabel = isMediaMode ? "图片/视频说明" : "记录内容";

  /** 切换动态标签选择。 */
  function handleToggleLabel(label: string, checked: boolean) {
    const nextLabels = checked
      ? [...draft.selectedLabels, label]
      : draft.selectedLabels.filter((item) => item !== label);
    const nextDraft = {
      ...draft,
      selectedLabels: dedupeText(nextLabels),
    };
    if (
      showLabels &&
      nextDraft.selectedLabels.some((item) => CONTENT_HIDING_LABELS.has(item))
    ) {
      nextDraft.content = "";
    }
    onDraftChange(nextDraft);
  }

  return (
    <section
      className={[
        "crm-contact-workbench-detail-section",
        "crm-contact-workbench-detail-section--composer",
        actionPlacement === "footer" ? "is-action-footer" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="crm-contact-workbench-detail-title">
        <Typography.Title heading={6}>动态添加</Typography.Title>
        {actionPlacement === "title" ? (
          <Button
            type="primary"
            icon={<Plus size={15} />}
            loading={mutating}
            disabled={!canRecord || uploading || failed.length > 0 || !isRecordDraftSubmittable(draft, selectedType)}
            onClick={onSubmit}
          >
            保存
          </Button>
        ) : null}
      </div>
      <div className="crm-contact-workbench-detail-scroll">
      <div className="crm-contact-workbench-composer-fields">
      <div className="crm-contact-workbench-field">
        <span>记录类型</span>
        <Select
          aria-label="记录类型"
          disabled={uploading || mutating}
          value={draft.recordTypeCode}
          onChange={(recordTypeCode) =>
            onDraftChange({
              ...draft,
              recordTypeCode: String(recordTypeCode),
              meetingName: "",
              meetingDesc: "",
              selectedLabels: [],
              content: "",
              attachments: [],
            })
          }
        >
          {recordTypeOptions.map((option) => (
            <Select.Option key={option.code} value={option.code}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      </div>
      {showLabels && recordLabelGroups.length > 0 ? (
        <div className="crm-contact-workbench-field">
          <span>动态标签</span>
          <div className="crm-contact-workbench-record-label-groups">
            {recordLabelGroups.map((group) => (
              <div className="crm-contact-workbench-record-label-group" key={group.groupCode}>
                <div className="crm-contact-workbench-record-label-group__title">
                  {group.groupLabel}
                </div>
                <div className="crm-contact-workbench-record-label-options">
                  {group.labels.map((label) => (
                    <Checkbox
                      key={`${group.groupCode}-${label.label}`}
                      checked={draft.selectedLabels.includes(label.label)}
                      onChange={(checked) => handleToggleLabel(label.label, Boolean(checked))}
                    >
                      {label.label}
                    </Checkbox>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {isMeetingMode ? (
        <>
          <label className="crm-contact-workbench-field">
            <span>会议名称</span>
            <Input
              placeholder="输入【会议赛事比赛】名称"
              value={draft.meetingName}
              onChange={(meetingName) => onDraftChange({ ...draft, meetingName })}
            />
          </label>
          <label className="crm-contact-workbench-field">
            <span>会议详情</span>
            <Input.TextArea
              autoSize={{ minRows: 5, maxRows: 8 }}
              placeholder="详细描述【会议赛事比赛】"
              value={draft.meetingDesc}
              onChange={(meetingDesc) => onDraftChange({ ...draft, meetingDesc })}
            />
          </label>
        </>
      ) : null}
      {isMediaMode ? (
        <div className="crm-contact-workbench-field">
          <span>
            照片/视频上传
            <i className="crm-contact-workbench-required-mark"> *</i>
          </span>
          <div className="crm-contact-workbench-media-upload">
            <Upload
              {...uploadProps}
              style={{ width: "100%", minWidth: 0 }}
              accept={{ type: "image/*,video/*", strict: false }}
              multiple
              listType="text"
              disabled={mutating || !canRecord}
              showUploadList={{
                cancelIcon: null,
                startIcon: null,
                fileName: (file) => (
                  <span className="crm-contact-workbench-upload-file">
                    <span className="crm-contact-workbench-upload-file-name" title={file.name}>
                      {file.status === "done" ? <a href={file.url ? resolveHttpResourceUrl(file.url) : undefined} target="_blank" rel="noreferrer">{file.name}</a> : file.name}
                    </span>
                    <span className="crm-contact-workbench-upload-file-status">
                      {file.status === "init" ? "等待上传" : null}
                      {file.status === "uploading" ? (file.percent === 100 ? "等待服务器处理" : `上传中 ${file.percent ?? 0}%`) : null}
                      {file.status === "done" ? "已上传" : null}
                      {file.status === "error" ? "上传失败" : null}
                    </span>
                  </span>
                ),
              }}
              tip={<span className="crm-contact-workbench-upload-tip">支持多选图片和视频；单张图片不超过 20MB，单个视频不超过 150MB</span>}
            >
              <Button icon={<Plus size={14} />} disabled={mutating || !canRecord}>
                选择图片/视频
              </Button>
            </Upload>
            {failed.map((file) => (
              <div className="crm-contact-workbench-media-error" role="alert" key={file.uid}>
                {file.name}：{file.response?.message ?? "上传失败，请重试"}
              </div>
            ))}
            <Typography.Text type="secondary" className="crm-contact-workbench-upload-summary" aria-live="polite">
              {uploading ? "正在上传，请等待全部文件完成后保存" : failed.length > 0 ? "请重试或移除失败文件后保存" : draft.attachments.length > 0 ? `已上传 ${draft.attachments.length} 个附件` : "请至少上传一张图片或一段视频"}
            </Typography.Text>
          </div>
        </div>
      ) : null}
      {showContent ? (
        <label className="crm-contact-workbench-field" htmlFor="crm-contact-record-content">
          <span>{contentLabel}</span>
          <Input.TextArea
            id="crm-contact-record-content"
            autoSize={{ minRows: 5, maxRows: 8 }}
            value={draft.content}
            onChange={(content) => onDraftChange({ ...draft, content })}
          />
        </label>
      ) : null}
      </div>
      </div>
      {actionPlacement === "footer" ? (
        <div className="crm-contact-workbench-detail-action-bar">
          <Button
            type="primary"
            long
            icon={<Plus size={15} />}
            loading={mutating}
            disabled={!canRecord || uploading || failed.length > 0 || !isRecordDraftSubmittable(draft, selectedType)}
            onClick={onSubmit}
          >
            保存
          </Button>
        </div>
      ) : null}
    </section>
  );
}

/** 判断动态表单是否满足当前类型的最低保存条件。 */
function isRecordDraftSubmittable(
  draft: ContactRecordDraft,
  selectedType: Pick<ContactWorkbenchRecordTypeOption, "formMode">,
) {
  if (selectedType.formMode === "meeting") {
    return Boolean(draft.meetingName.trim() && draft.meetingDesc.trim());
  }
  if (selectedType.formMode === "media_note") {
    return Boolean(draft.content.trim() && draft.attachments.length > 0);
  }
  if (
    selectedType.formMode === "content" &&
    draft.selectedLabels.some((label) => CONTENT_HIDING_LABELS.has(label))
  ) {
    return true;
  }
  return Boolean(draft.content.trim());
}

/** 粗略识别视频地址，用于列表文案。 */
function isVideoUrl(url: string) {
  return /\.(mp4|mov|mkv|webm|avi)(\?|$)/i.test(url);
}

/** 拆分动态标签快照为独立展示项。 */
export function splitContactRecordLabels(labelsSnapshot?: string) {
  if (!labelsSnapshot) return [];
  return labelsSnapshot
    .split("▲")
    .map((item) => item.trim())
    .filter(Boolean);
}

/** 按标签语义返回列表展示色，突出动态标签列层次。 */
export function resolveContactRecordLabelColor(label: string): string {
  if (CONTENT_HIDING_LABELS.has(label) || OTHER_STATUS_LABELS.has(label)) {
    return "orangered";
  }
  if (FOLLOW_UP_LABELS.has(label)) {
    return "purple";
  }
  if (label.includes("出行") || label.includes("合作")) {
    return "arcoblue";
  }
  return "cyan";
}

/** 去除重复动态标签。 */
function dedupeText(values: string[]) {
  return values.filter((item, index) => item.trim() && values.indexOf(item) === index);
}
